/**
 * Knowledge gap store — tracks what agents searched for and couldn't find.
 * BET-89: Knowledge Gap Sensing.
 *
 * Dual layer:
 *   In-memory  — session-scoped, fast, used for wrapup/start surfacing.
 *   Persistent — fire-and-forget writes to Convex via `gaps.record`.
 *                Cross-session, admin-only reads. Survives restarts.
 *
 * Gaps are keyed by agent session ID so concurrent sessions don't leak
 * state across each other. Sessionless callers (e.g. stdio transport)
 * use a fallback "__sessionless__" key.
 *
 * Deduplicates within a 60s window to avoid noise from confused agents
 * retrying the same query.
 */

import { kernelMutation, getAgentSessionId } from "./client.js";

export type GapType =
  | "search_zero"
  | "context_task_empty"
  | "context_entry_isolated"
  | "context_graph_empty";

export interface KnowledgeGap {
  query: string;
  tool: string;
  action: string;
  gapType: GapType;
  collectionScope?: string;
  timestamp: number;
}

const SESSIONLESS_KEY = "__sessionless__";
const sessionGaps = new Map<string, KnowledgeGap[]>();
const DEDUP_WINDOW_MS = 60_000;

function currentSessionKey(): string {
  return getAgentSessionId() ?? SESSIONLESS_KEY;
}

function getGapsForSession(sessionKey: string): KnowledgeGap[] {
  let gaps = sessionGaps.get(sessionKey);
  if (!gaps) {
    gaps = [];
    sessionGaps.set(sessionKey, gaps);
  }
  return gaps;
}

function isDuplicate(gaps: KnowledgeGap[], gap: KnowledgeGap): boolean {
  const cutoff = gap.timestamp - DEDUP_WINDOW_MS;
  return gaps.some(
    (existing) =>
      existing.query === gap.query &&
      existing.tool === gap.tool &&
      existing.action === gap.action &&
      existing.timestamp >= cutoff,
  );
}

/**
 * Record a gap in-memory and persist to Convex (fire-and-forget).
 * Returns true if the gap was new (not deduplicated).
 */
export function recordGap(gap: Omit<KnowledgeGap, "timestamp">): boolean {
  const timestamped: KnowledgeGap = { ...gap, timestamp: Date.now() };
  const sessionKey = currentSessionKey();
  const gaps = getGapsForSession(sessionKey);
  if (isDuplicate(gaps, timestamped)) return false;
  gaps.push(timestamped);

  persistGap(gap);
  return true;
}

function persistGap(gap: Omit<KnowledgeGap, "timestamp">): void {
  const sessionId = getAgentSessionId();
  kernelMutation("gaps.record", {
    sessionId: sessionId ?? undefined,
    query: gap.query,
    tool: gap.tool,
    action: gap.action,
    gapType: gap.gapType,
    collectionScope: gap.collectionScope,
  }).catch(() => {
    // Persistence is best-effort; in-memory store is the primary path.
  });
}

export function getSessionGaps(): readonly KnowledgeGap[] {
  return getGapsForSession(currentSessionKey());
}

/** Top gaps by frequency — returns unique queries ranked by how often they appeared. */
export function getTopGaps(limit = 5): Array<{ query: string; count: number; gapType: GapType }> {
  const gaps = getGapsForSession(currentSessionKey());
  const counts = new Map<string, { count: number; gapType: GapType }>();
  for (const gap of gaps) {
    const key = gap.query.toLowerCase().trim();
    const existing = counts.get(key);
    if (existing) {
      existing.count++;
    } else {
      counts.set(key, { count: 1, gapType: gap.gapType });
    }
  }
  return [...counts.entries()]
    .map(([query, { count, gapType }]) => ({ query, count, gapType }))
    .sort((a, b) => b.count - a.count)
    .slice(0, limit);
}

export function clearSessionGaps(): void {
  const sessionKey = currentSessionKey();
  sessionGaps.delete(sessionKey);
}

/**
 * Remove all gaps for a specific session ID.
 * Called during session teardown to prevent unbounded memory growth.
 */
export function cleanupSessionGaps(sessionId: string): void {
  sessionGaps.delete(sessionId);
}

/**
 * Resolve persistent gaps matching a newly captured entry.
 * Fire-and-forget — called from smart-capture after successful create.
 */
export function resolveGapsForEntry(entryName: string, entryId: string): void {
  kernelMutation("gaps.resolve", { entryName, entryId }).catch(() => {
    // Best-effort decay; failure doesn't affect capture flow.
  });
}
